import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from 'shared/components/UC/Badge';
import type { BadgeProps } from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import Checkbox from 'shared/components/UC/Checkbox';
import {
  ApplicationWithAdminData,
  EvalByEvaluatorAssignment,
  EvalByEvaluatorRow,
} from '../../types/admin';
import adminService from '../../services/adminService';
import { ALERT_CONFIG } from './alertCatalog';
import AssignmentOverflowMenu from './AssignmentOverflowMenu';
import EvaluatorAssignmentModal from './EvaluatorAssignmentModal';
import EvaluatorNotesModal from './EvaluatorNotesModal';
import GroupedMultiSelect from './GroupedMultiSelect';
import RemindModal from './RemindModal';
import SendInvitationsModal, {
  InvitationResult,
} from './SendInvitationsModal';
import styles from './EvalByEvaluator.module.css';

type EvaluatorViewMode = 'assigned' | 'all';

interface EvalByEvaluatorProps {
  rows: EvalByEvaluatorRow[];
  isLoading: boolean;
  onViewEvaluations: (applicationId: string) => void;
  onToast?: (msg: string) => void;
  formTemplateId?: string | null;
  /** Contenido del resumen (prosa) que se agrupa con el filterBar en la tarjeta. */
  toolsHeader?: React.ReactNode;
  /** Se llama tras enviar invitaciones, para que el padre recargue los tableros. */
  onDataChanged?: () => void;
  /** Arranca con el filtro "Con invitaciones sin enviar" aplicado. */
  initialInvitationFilter?: boolean;
}

interface RemindModalState {
  evaluatorId: string;
  evaluatorName: string;
  assignments: Array<{
    assignmentId: string;
    applicationNumber: string;
    title: string;
    status: 'pending' | 'accepted_not_started' | 'in_progress';
  }>;
}

interface NotesModalState {
  evaluatorId: string;
  evaluatorName: string;
  isExternal: boolean;
}

// ── Assignment status display config ─────────────────────────────────────────
const ASSIGNMENT_STATUS_CONFIG: Record<
  EvalByEvaluatorAssignment['status'],
  { label: string; variant: BadgeProps['variant'] }
> = {
  pending:              { label: 'Sin respuesta',       variant: 'secondary' },
  accepted_not_started: { label: 'Sin iniciar',         variant: 'secondary' },
  in_progress:          { label: 'En evaluación',       variant: 'info'      },
  completed:            { label: 'Entregada',           variant: 'success'   },
  declined:             { label: 'Excusada',            variant: 'warning'   }, // label overridden below when declineReason is present
  conflict_declared:    { label: 'Conflicto declarado', variant: 'warning'   },
};

// ── Sortable columns ──────────────────────────────────────────────────────────
type SortCol =
  | 'evaluatorName'
  | 'totalInvitations'
  | 'notInvitedCount'
  | 'acceptedCount'
  | 'pendingEvals'
  | 'disponible'
  | 'pendingCount'
  | 'declinedOrConflict'
  | 'maxDaysPending'
  | 'daysSinceActivity';

// ── Progress bar helpers ──────────────────────────────────────────────────────
// denominator = totalInvitations - declined - conflict (pending still counts; only explicit opt-outs excluded)
function getBarWidths(
  completed: number,
  inProgress: number,
  notStarted: number,
  pending: number,
  denominator: number
): { completedPct: number; inProgressPct: number; notStartedPct: number; pendingPct: number } {
  if (denominator === 0) return { completedPct: 0, inProgressPct: 0, notStartedPct: 0, pendingPct: 0 };
  return {
    completedPct: (completed / denominator) * 100,
    inProgressPct: (inProgress / denominator) * 100,
    notStartedPct: (notStarted / denominator) * 100,
    pendingPct: (pending / denominator) * 100,
  };
}

function isRemindable(a: { status: string }): boolean {
  return a.status === 'pending' || a.status === 'accepted_not_started' || a.status === 'in_progress';
}

// ── Component ─────────────────────────────────────────────────────────────────
const EvalByEvaluator: React.FC<EvalByEvaluatorProps> = ({
  rows,
  isLoading,
  onViewEvaluations,
  onToast,
  formTemplateId,
  toolsHeader,
  onDataChanged,
  initialInvitationFilter = false,
}) => {
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sortCol, setSortCol] = useState<SortCol>('disponible');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<EvaluatorViewMode>('assigned');
  const [allRows, setAllRows] = useState<EvalByEvaluatorRow[] | null>(null);
  const [allRowsLoading, setAllRowsLoading] = useState(false);
  const allRowsFetched = useRef(false);
  const [remindModal, setRemindModal] = useState<RemindModalState | null>(null);
  const [notesModal, setNotesModal] = useState<NotesModalState | null>(null);
  const [assignModal, setAssignModal] = useState<ApplicationWithAdminData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCupo, setFilterCupo] = useState<string[]>([]);
  const [filterInvit, setFilterInvit] = useState<string[]>(
    initialInvitationFilter ? ['not_invited'] : []
  );
  // Selección para el envío masivo. A diferencia de la tabla del asistente de
  // importación —donde marcar significa DESCARTAR—, aquí marcar significa
  // enviar. Son acciones opuestas y por eso la semántica se invierte.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<InvitationResult | null>(null);
  const [profileOverrides, setProfileOverrides] = useState<
    Map<string, { score: number | null; latestNote: string | null }>
  >(new Map());

  // Fetch all evaluators (including unassigned) lazily when the user switches view
  useEffect(() => {
    if (viewMode !== 'all' || allRowsFetched.current) return;
    allRowsFetched.current = true;
    setAllRowsLoading(true);
    adminService
      .getEvaluatorBoardData(formTemplateId, true)
      .then((data) => { setAllRows(data); setAllRowsLoading(false); })
      .catch(() => setAllRowsLoading(false));
  }, [viewMode, formTemplateId]);

  const displayRows = viewMode === 'all' && allRows !== null ? allRows : rows;
  const displayLoading = viewMode === 'all' ? allRowsLoading : isLoading;

  const handleAssignClick = async (applicationId: string) => {
    try {
      const app = await adminService.getApplicationById(applicationId);
      setAssignModal(app);
    } catch {
      onToast?.('Error al cargar la postulación');
    }
  };

  const handleProfileChanged = async (evaluatorId: string) => {
    const profile: any = await adminService.getEvaluatorProfile(evaluatorId);
    const notes: any[] = profile.notes ?? [];
    setProfileOverrides((prev) => {
      const next = new Map(prev);
      next.set(evaluatorId, {
        score: profile.score ?? null,
        latestNote: notes.length ? notes[notes.length - 1].text : null,
      });
      return next;
    });
  };

  const toggleExpand = (evaluatorId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(evaluatorId)) {
        next.delete(evaluatorId);
      } else {
        next.add(evaluatorId);
      }
      return next;
    });
  };

  const toggleSort = (col: SortCol) => {
    setSortDir((d) => (sortCol === col && d === 'asc' ? 'desc' : 'asc'));
    setSortCol(col);
  };

  const sortIconClass = (col: SortCol) =>
    sortCol === col ? styles.sortIconActive : styles.sortIcon;

  const sortIconName = (col: SortCol) =>
    sortCol === col && sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';

  const ariaSortAttr = (
    col: SortCol
  ): React.ThHTMLAttributes<HTMLTableCellElement>['aria-sort'] => {
    if (sortCol !== col) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
  };

  const handleDownloadCSV = () => {
    const esc = (v: string | number | null) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Evaluador', 'Tipo', 'Email', 'Asign.', 'Sin invitar', 'Sin resp.', 'Pendiente', 'Aceptadas', 'Declinadas - Excusada', 'Declinadas - Conflicto declarado', 'Cupo libre', 'Máx. días', 'Últ. acceso (días)', 'Score', 'Nota'];
    const csvRows = [
      headers.join(','),
      ...sorted.map((r) => {
        const profile = profileOverrides.get(r.evaluatorId) ?? r.adminProfile;
        return [
          esc(r.evaluatorName),
          esc(r.isExternal ? 'externo' : 'interno'),
          esc(r.email),
          r.totalInvitations,
          r.notInvitedCount,
          r.pendingCount - r.notInvitedCount,
          r.activeLoad - r.pendingCount,
          r.acceptedCount,
          r.declinedCount,
          r.conflictCount,
          r.disponible,
          r.maxDaysPending === -1 ? '' : r.maxDaysPending,
          r.daysSinceActivity === -1 ? '' : Math.round(r.daysSinceActivity),
          profile.score ?? '',
          esc(profile.latestNote ?? ''),
        ].join(',');
      }),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = viewMode === 'all' ? 'todos_los_evaluadores.csv' : 'evaluadores_con_asignaciones.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Opciones del filtro de cupo, con conteo por categoría (sobre displayRows).
  const cupoOptions = useMemo(() => {
    const counts = { overloaded: 0, zero: 0, available: 0 };
    displayRows.forEach((r) => {
      if (r.disponible < 0) counts.overloaded += 1;
      else if (r.disponible === 0) counts.zero += 1;
      else counts.available += 1;
    });
    return [
      { value: 'overloaded', label: `Sobrecargados (${counts.overloaded})` },
      { value: 'zero', label: `Sin cupo (${counts.zero})` },
      { value: 'available', label: `Con cupo disponible (${counts.available})` },
    ];
  }, [displayRows]);

  // Filtro de invitaciones: el que usa el administrador para encontrar a quién
  // le falta el correo.
  const invitOptions = useMemo(() => {
    const pendientes = displayRows.filter((r) => r.notInvitedCount > 0).length;
    return [
      { value: 'not_invited', label: `Con invitaciones sin enviar (${pendientes})` },
    ];
  }, [displayRows]);

  // Filter by alert preset, search, facet filters — then sort
  const sorted = useMemo(() => {
    let result = displayRows;
    if (filterCupo.length > 0)
      result = result.filter((r) => {
        const cat =
          r.disponible < 0 ? 'overloaded' : r.disponible === 0 ? 'zero' : 'available';
        return filterCupo.includes(cat);
      });

    if (filterInvit.includes('not_invited'))
      result = result.filter((r) => r.notInvitedCount > 0);

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter((r) => r.evaluatorName.toLowerCase().includes(q));
    }
const getValue = (r: EvalByEvaluatorRow, col: SortCol): number | string => {
      if (col === 'evaluatorName') return r.evaluatorName;
      if (col === 'maxDaysPending') return r.maxDaysPending === -1 ? -Infinity : r.maxDaysPending;
      if (col === 'daysSinceActivity') return r.daysSinceActivity === -1 ? -Infinity : r.daysSinceActivity;
      if (col === 'pendingEvals') return r.activeLoad - r.pendingCount;
      if (col === 'disponible') return r.disponible;
      if (col === 'declinedOrConflict') return r.declinedCount + r.conflictCount;
      return r[col] as number;
    };

    return [...result].sort((a, b) => {
      const av = getValue(a, sortCol);
      const bv = getValue(b, sortCol);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv), 'es')
        : String(bv).localeCompare(String(av), 'es');
    });
  }, [displayRows, filterCupo, filterInvit, searchTerm, sortCol, sortDir]);

  // ── Selección para el envío masivo ──────────────────────────────────────────
  // Solo participan los evaluadores que tienen algo sin invitar.
  const invitables = sorted.filter((r) => r.notInvitedCount > 0);
  const selectedRows = invitables.filter((r) => selectedIds.has(r.evaluatorId));
  const selectedAssignments = selectedRows.reduce((sum, r) => sum + r.notInvitedCount, 0);
  const allInvitablesSelected =
    invitables.length > 0 && selectedRows.length === invitables.length;

  const toggleRowSelection = (evaluatorId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(evaluatorId)) next.delete(evaluatorId);
      else next.add(evaluatorId);
      return next;
    });
  };

  // "Seleccionar todo" opera sobre las filas FILTRADAS, no sobre las visibles:
  // si el administrador filtró por "con invitaciones sin enviar", espera que
  // abarque todo el resultado del filtro.
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedRows.length > 0 && !allInvitablesSelected;
    }
  });

  const toggleSelectAll = () => {
    setSelectedIds(
      allInvitablesSelected ? new Set() : new Set(invitables.map((r) => r.evaluatorId))
    );
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await adminService.sendInvitations(
        selectedRows.map((r) => r.evaluatorId),
        formTemplateId
      );
      setSendResult({
        emails: res.emails,
        assignments: res.assignments,
        newEvaluators: res.newEvaluators,
      });
      setSelectedIds(new Set());
    } catch {
      onToast?.('No se pudieron enviar las invitaciones');
      setSendModalOpen(false);
    } finally {
      setSending(false);
    }
  };

  // Al cerrar el acuse recién se recarga: refrescar antes haría desaparecer el
  // mensaje de éxito mientras el tablero vuelve a cargar.
  const handleCloseSendModal = () => {
    setSendModalOpen(false);
    if (sendResult) {
      setSendResult(null);
      onDataChanged?.();
    }
  };

  if (displayLoading) {
    return (
      <div className={styles.wrapper}>
        {toolsHeader && <div className={styles.toolsCard}>{toolsHeader}</div>}
        <div className={styles.loading}>
          <div className={styles.spinner} aria-hidden="true" />
          <span>Cargando datos por evaluador...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Tarjeta de herramientas: KPIs (slot) + banner + filtros agrupados;
          la tabla queda afuera, abajo. */}
      <div className={styles.toolsCard}>
      {toolsHeader}
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.viewModeSelectWrapper}>
          <select
            className={styles.viewModeSelect}
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as EvaluatorViewMode)}
            aria-label="Vista de evaluadores"
          >
            <option value="assigned">Evaluadores con asignaciones</option>
            <option value="all">Todos los evaluadores</option>
          </select>
        </div>
        <GroupedMultiSelect
          groups={[{ options: cupoOptions }]}
          selected={filterCupo}
          onChange={setFilterCupo}
          placeholder="Cupo"
        />
        <GroupedMultiSelect
          groups={[{ options: invitOptions }]}
          selected={filterInvit}
          onChange={setFilterInvit}
          placeholder="Invitaciones"
        />
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Buscar por nombre o apellido"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Buscar evaluador"
        />
        <Button variant="primary" size="sm" onClick={handleDownloadCSV}>
          Descargar CSV
        </Button>
      </div>
      </div>

      {sorted.length === 0 ? (
        <div className={styles.empty}>
          {displayRows.length === 0
            ? 'No hay evaluadores con asignaciones para este concurso.'
            : 'Ningún evaluador coincide con los filtros seleccionados.'}
        </div>
      ) : (
      <div className={styles.tableContainer}>
        <table
          className={styles.table}
          aria-label="Tablero de evaluaciones por evaluador"
        >
          <thead>
            <tr>
              {/* Selección para el envío masivo de invitaciones */}
              <th className={`${styles.th} ${styles.checkboxCell}`}>
                <Checkbox
                  ref={selectAllRef}
                  id="eval-select-all"
                  label={
                    <span className={styles.srOnly}>
                      Seleccionar todos los evaluadores con invitaciones sin enviar
                    </span>
                  }
                  checked={allInvitablesSelected}
                  onChange={toggleSelectAll}
                  disabled={invitables.length === 0}
                />
              </th>

              {/* Evaluador (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('evaluatorName')}
                aria-sort={ariaSortAttr('evaluatorName')}
              >
                <span className={styles.sortHeaderContent}>
                  Evaluador
                  <i className={`uc-icon icon-size--sm ${sortIconClass('evaluatorName')}`}>
                    {sortIconName('evaluatorName')}
                  </i>
                </span>
              </th>

              {/* Invitaciones (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('totalInvitations')}
                aria-sort={ariaSortAttr('totalInvitations')}
              >
                <span className={styles.sortHeaderContent}>
                  Asign.
                  <i className={`uc-icon icon-size--sm ${sortIconClass('totalInvitations')}`}>
                    {sortIconName('totalInvitations')}
                  </i>
                </span>
              </th>

              {/* Sin resp. (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('pendingCount')}
                aria-sort={ariaSortAttr('pendingCount')}
              >
                <span className={styles.sortHeaderContent}>
                  Sin resp.
                  <i className={`uc-icon icon-size--sm ${sortIconClass('pendingCount')}`}>
                    {sortIconName('pendingCount')}
                  </i>
                </span>
              </th>

              {/* Sin invitar (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('notInvitedCount')}
                aria-sort={ariaSortAttr('notInvitedCount')}
              >
                <span className={styles.sortHeaderContent}>
                  Sin invitar
                  <i className={`uc-icon icon-size--sm ${sortIconClass('notInvitedCount')}`}>
                    {sortIconName('notInvitedCount')}
                  </i>
                </span>
              </th>

              {/* Pendiente (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('pendingEvals')}
                aria-sort={ariaSortAttr('pendingEvals')}
              >
                <span className={styles.sortHeaderContent}>
                  Pendiente
                  <i className={`uc-icon icon-size--sm ${sortIconClass('pendingEvals')}`}>
                    {sortIconName('pendingEvals')}
                  </i>
                </span>
              </th>

              {/* Aceptadas (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('acceptedCount')}
                aria-sort={ariaSortAttr('acceptedCount')}
              >
                <span className={styles.sortHeaderContent}>
                  Aceptadas
                  <i className={`uc-icon icon-size--sm ${sortIconClass('acceptedCount')}`}>
                    {sortIconName('acceptedCount')}
                  </i>
                </span>
              </th>

              {/* Declinadas (declined + conflicto fusionados, sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('declinedOrConflict')}
                aria-sort={ariaSortAttr('declinedOrConflict')}
              >
                <span className={styles.sortHeaderContent}>
                  Declinadas
                  <i className={`uc-icon icon-size--sm ${sortIconClass('declinedOrConflict')}`}>
                    {sortIconName('declinedOrConflict')}
                  </i>
                </span>
              </th>

              {/* Disponible (sortable + tooltip) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('disponible')}
                aria-sort={ariaSortAttr('disponible')}
              >
                <span className={styles.sortHeaderContent}>
                  <span className={styles.disponibleHeader}>
                    Cupo libre
                    <span className={styles.helpTooltip} aria-label="Ayuda sobre cupo libre">
                      ?
                      <span className={styles.tooltipContent} role="tooltip">
                        Cupos libres para nuevas asignaciones. Fórmula: máximo del concurso − cuota consumida. La cuota es acumulativa: cada asignación consume un cupo permanentemente, incluso si la evaluación ya fue enviada. Solo las declinadas y los conflictos no consumen cupo.
                      </span>
                    </span>
                  </span>
                  <i className={`uc-icon icon-size--sm ${sortIconClass('disponible')}`}>
                    {sortIconName('disponible')}
                  </i>
                </span>
              </th>

              {/* Progreso (no sort) */}
              <th className={styles.th}>
                <span className={styles.progressHeader}>
                  Progreso de aceptadas
                  <span className={styles.helpTooltip} aria-label="Ayuda sobre la barra de progreso">
                    ?
                    <span className={styles.tooltipContent} role="tooltip">
                      Verde = evaluaciones entregadas · Azul = en evaluación · Ámbar = aceptadas sin iniciar · Gris = invitaciones sin respuesta.
                      El número X/Y muestra evaluaciones entregadas sobre el total de invitaciones activas.
                    </span>
                  </span>
                </span>
              </th>

              {/* Máx. días sin entregar (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('maxDaysPending')}
                aria-sort={ariaSortAttr('maxDaysPending')}
              >
                <span className={styles.sortHeaderContent}>
                  Máx. días
                  <i className={`uc-icon icon-size--sm ${sortIconClass('maxDaysPending')}`}>
                    {sortIconName('maxDaysPending')}
                  </i>
                </span>
              </th>

              {/* Último acceso (sortable) */}
              <th
                className={`${styles.th} ${styles.sortableHeader}`}
                onClick={() => toggleSort('daysSinceActivity')}
                aria-sort={ariaSortAttr('daysSinceActivity')}
              >
                <span className={styles.sortHeaderContent}>
                  Últ. acceso
                  <i className={`uc-icon icon-size--sm ${sortIconClass('daysSinceActivity')}`}>
                    {sortIconName('daysSinceActivity')}
                  </i>
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {sorted.map((row) => {
              const isExpanded = expandedIds.has(row.evaluatorId);
              const adminProfile = profileOverrides.get(row.evaluatorId) ?? row.adminProfile;
              // Exclude explicit opt-outs; pending invitations still count as expected work
              const progressDenominator =
                row.totalInvitations - row.declinedCount - row.conflictCount;
              const { completedPct, inProgressPct, notStartedPct, pendingPct } = getBarWidths(
                row.completedCount,
                row.inProgressCount,
                row.acceptedNotStartedCount,
                row.pendingCount,
                progressDenominator
              );
              const progressAriaLabel = `${row.completedCount} de ${progressDenominator} entregadas. En evaluación: ${row.inProgressCount}. Sin iniciar: ${row.acceptedNotStartedCount}. Sin respuesta: ${row.pendingCount}.`;

              return (
                <React.Fragment key={row.evaluatorId}>
                  {/* ── Collapsed (main) row ── */}
                  <tr
                    className={
                      isExpanded
                        ? `${styles.rowCollapsed} ${styles.rowExpandedHeader}`
                        : styles.rowCollapsed
                    }
                    onClick={() => toggleExpand(row.evaluatorId)}
                    aria-expanded={isExpanded}
                  >
                    {/* Selección */}
                    <td
                      className={`${styles.td} ${styles.checkboxCell}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.notInvitedCount > 0 && (
                        <Checkbox
                          id={`eval-select-${row.evaluatorId}`}
                          label={
                            <span className={styles.srOnly}>
                              Enviar invitaciones a {row.evaluatorName}
                            </span>
                          }
                          checked={selectedIds.has(row.evaluatorId)}
                          onChange={() => toggleRowSelection(row.evaluatorId)}
                        />
                      )}
                    </td>

                    {/* Evaluador */}
                    <td className={styles.td}>
                      <div className={styles.evaluatorCell}>
                        <i className={`uc-icon ${styles.chevron}`} aria-hidden="true">
                          {isExpanded ? 'expand_less' : 'expand_more'}
                        </i>
                        <div className={styles.evaluatorName}>{row.evaluatorName}</div>
                      </div>
                    </td>

                    {/* Invit. */}
                    <td className={styles.td}>{row.totalInvitations}</td>

                    {/* Sin resp. — solo los invitados que aún no responden: a
                        quien nunca fue avisado no se le puede exigir respuesta. */}
                    {(() => {
                      const sinResponder = row.pendingCount - row.notInvitedCount;
                      return (
                        <td className={styles.td}>
                          {sinResponder > 0 ? (
                            <span className={styles.alertChip}>{sinResponder}</span>
                          ) : sinResponder}
                        </td>
                      );
                    })()}

                    {/* Sin invitar */}
                    <td className={styles.td}>
                      {row.notInvitedCount > 0 ? (
                        <span className={styles.alertChip}>{row.notInvitedCount}</span>
                      ) : row.notInvitedCount}
                    </td>

                    {/* Pendiente */}
                    <td className={styles.td}>{row.activeLoad - row.pendingCount}</td>

                    {/* Aceptadas */}
                    <td className={styles.td}>{row.acceptedCount}</td>

                    {/* Declinadas (declined + conflicto) */}
                    {(() => {
                      const declinadas = row.declinedCount + row.conflictCount;
                      return (
                        <td className={styles.td}>
                          {declinadas > 0 ? (
                            <span className={styles.alertChip}>{declinadas}</span>
                          ) : declinadas}
                        </td>
                      );
                    })()}

                    {/* Disponible */}
                    <td className={styles.td}>
                      <span className={[
                        styles.disponibleChip,
                        row.disponible <= 0 ? styles.disponibleDanger
                          : row.disponible >= ALERT_CONFIG.OVERLOAD_THRESHOLD ? styles.disponibleFull
                          : row.disponible >= 6 ? styles.disponibleMedium
                          : styles.disponibleLow,
                      ].join(' ')}>
                        {row.disponible}
                      </span>
                    </td>

                    {/* Progreso */}
                    <td className={styles.td}>
                      <div className={styles.progressCell}>
                        <div
                          className={styles.progressBar}
                          role="img"
                          aria-label={progressAriaLabel}
                        >
                          {completedPct > 0 && (
                            <span
                              className={`${styles.progressSegment} ${styles.progressCompleted}`}
                              style={{ width: `${completedPct}%` }}
                            />
                          )}
                          {inProgressPct > 0 && (
                            <span
                              className={`${styles.progressSegment} ${styles.progressInProgress}`}
                              style={{ width: `${inProgressPct}%` }}
                            />
                          )}
                          {notStartedPct > 0 && (
                            <span
                              className={`${styles.progressSegment} ${styles.progressNotStarted}`}
                              style={{ width: `${notStartedPct}%` }}
                            />
                          )}
                          {pendingPct > 0 && (
                            <span
                              className={`${styles.progressSegment} ${styles.progressPending}`}
                              style={{ width: `${pendingPct}%` }}
                            />
                          )}
                        </div>
                        <span className={styles.progressText}>
                          {row.completedCount}/{progressDenominator}
                        </span>
                      </div>
                    </td>

                    {/* Máx. días sin entregar */}
                    <td className={styles.td}>
                      {row.maxDaysPending === -1 ? (
                        <span className={styles.muted}>—</span>
                      ) : row.maxDaysPending > 11 ? (
                        <span className={styles.alertChip}>{Math.round(row.maxDaysPending)} d</span>
                      ) : (
                        `${Math.round(row.maxDaysPending)} d`
                      )}
                    </td>

                    {/* Último acceso */}
                    <td className={styles.td}>
                      {row.daysSinceActivity === -1
                        ? <span className={styles.muted}>Nunca</span>
                        : `${Math.round(row.daysSinceActivity)} d`}
                    </td>

                  </tr>

                  {/* ── Expanded panel row ── */}
                  {isExpanded && (
                    <tr className={styles.rowExpanded}>
                      <td className={styles.td} colSpan={12} style={{ padding: 0 }}>
                        <div
                          className={styles.expandPanel}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* A) Metadata contextual del evaluador */}
                          <div className={styles.evaluatorExpandedMeta}>
                            <Badge label={row.isExternal ? 'Externo' : 'Interno UC'} variant="secondary" />
                            {row.email && (
                              <span className={styles.evaluatorEmail}>{row.email}</span>
                            )}
                          </div>

                          {/* B) Acciones a nivel evaluador */}
                          <div className={styles.actionsToolbar}>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={!row.assignments.some(isRemindable)}
                              onClick={() => {
                                const eligible = row.assignments.filter(isRemindable);
                                if (eligible.length === 0) {
                                  onToast?.('No hay asignaciones pendientes para recordar.');
                                  return;
                                }
                                setRemindModal({
                                  evaluatorId: row.evaluatorId,
                                  evaluatorName: row.evaluatorName,
                                  assignments: eligible.map((a) => ({
                                    assignmentId: a.assignmentId,
                                    applicationNumber: a.applicationNumber,
                                    title: a.title,
                                    status: a.status as 'pending' | 'accepted_not_started' | 'in_progress',
                                  })),
                                });
                              }}
                            >
                              Recordar pendientes
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                setNotesModal({
                                  evaluatorId: row.evaluatorId,
                                  evaluatorName: row.evaluatorName,
                                  isExternal: row.isExternal,
                                })
                              }
                            >
                              Notas / score
                            </Button>
                            {(adminProfile.score !== null || adminProfile.latestNote) && (
                              <div className={styles.profileSummary}>
                                {adminProfile.score !== null && (
                                  <span className={styles.scoreChipInline}>
                                    <i className="uc-icon">star</i>
                                    {adminProfile.score.toFixed(1)}
                                  </span>
                                )}
                                {adminProfile.latestNote && (
                                  <span className={styles.noteChipInline} title={adminProfile.latestNote}>
                                    {adminProfile.latestNote}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* C) Lista de asignaciones */}
                          {row.assignments.length > 0 && (
                            <table className={styles.assignmentTable}>
                              <thead>
                                <tr>
                                  <th>Folio</th>
                                  <th>Proyecto</th>
                                  <th>Estado evaluación</th>
                                  <th>Días sin respuesta</th>
                                  {/* DEV NOTE: en producción "Días evaluando" debería medirse desde
                                      acceptedAt (no desde assignedAt). El modelo actual no guarda
                                      acceptedAt; agregar ese campo al Assignment en backend. */}
                                  <th>Días evaluando</th>
                                  <th style={{ textAlign: 'right' }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.assignments.map((asgn) => {
                                  const isOptOut =
                                    asgn.status === 'declined' ||
                                    asgn.status === 'conflict_declared';
                                  const statusCfg =
                                    ASSIGNMENT_STATUS_CONFIG[asgn.status] ??
                                    ASSIGNMENT_STATUS_CONFIG.pending;
                                  return (
                                    <tr
                                      key={asgn.assignmentId}
                                      className={isOptOut ? styles.rowOptOut : undefined}
                                    >
                                      <td className={styles.assignmentFolio}>
                                        <div className={styles.folioCell}>
                                          <button
                                            className={styles.assignmentFolioLink}
                                            onClick={() => navigate(`/admin/application/${asgn.applicationId}`)}
                                          >
                                            {asgn.applicationNumber}
                                          </button>
                                          <button
                                            className={styles.folioNewTab}
                                            onClick={() => window.open(`/admin/application/${asgn.applicationId}`, '_blank')}
                                            aria-label="Abrir en pestaña nueva"
                                            title="Abrir en pestaña nueva"
                                          >
                                            <i className="uc-icon icon-size--sm" aria-hidden="true">open_in_new</i>
                                          </button>
                                        </div>
                                      </td>
                                      <td
                                        className={styles.assignmentTitle}
                                        title={asgn.title}
                                      >
                                        {asgn.title}
                                      </td>
                                      <td>
                                        {(() => {
                                          const DECLINE_LABELS: Record<string, string> = {
                                            thematic: 'Área no afín',
                                            conflict: 'Conflicto de interés',
                                            other: 'Otro motivo',
                                          };
                                          const label = asgn.status === 'declined' && asgn.declineReason
                                            ? DECLINE_LABELS[asgn.declineReason] ?? statusCfg.label
                                            : statusCfg.label;
                                          return (
                                            <span className={styles.declinedCellWrapper}>
                                              {/* "Sin invitar" reemplaza a "Sin respuesta": no se
                                                  puede esperar respuesta de quien no fue avisado.
                                                  Es lo que entra en el correo de este evaluador. */}
                                              <Badge
                                                label={asgn.notInvited ? 'Sin invitar' : label}
                                                variant={asgn.notInvited ? 'warning' : statusCfg.variant}
                                              />
                                              {asgn.status === 'declined' && asgn.declineNote && (
                                                <span className={styles.helpTooltip} tabIndex={0}>
                                                  i
                                                  <span className={styles.tooltipContent}>
                                                    {asgn.declineNote}
                                                  </span>
                                                </span>
                                              )}
                                            </span>
                                          );
                                        })()}
                                      </td>
                                      <td>
                                        {asgn.status === 'pending' && !asgn.notInvited
                                          ? `${Math.round(asgn.daysSinceAssigned)} d`
                                          : '—'}
                                      </td>
                                      <td>
                                        {asgn.status === 'accepted_not_started' || asgn.status === 'in_progress'
                                          ? `${Math.round(asgn.daysSinceAssigned)} d`
                                          : '—'}
                                      </td>
                                      <td className={styles.actionsCell}>
                                        {isOptOut ? (
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleAssignClick(asgn.applicationId)}
                                          >
                                            Reasignar
                                          </Button>
                                        ) : (
                                        <AssignmentOverflowMenu
                                          assignmentId={asgn.assignmentId}
                                          applicationId={asgn.applicationId}
                                          status={asgn.status}
                                          onViewApplication={onViewEvaluations}
                                          onRemind={() => {
                                            setRemindModal({
                                              evaluatorId: row.evaluatorId,
                                              evaluatorName: row.evaluatorName,
                                              assignments: [{
                                                assignmentId: asgn.assignmentId,
                                                applicationNumber: asgn.applicationNumber,
                                                title: asgn.title,
                                                status: asgn.status as 'pending' | 'accepted_not_started' | 'in_progress',
                                              }],
                                            });
                                          }}
                                          onReassign={() => handleAssignClick(asgn.applicationId)}
                                        />
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Barra de acción del envío masivo. Aparece solo con selección, y sigue
          el molde de .actionBar del asistente de importación. */}
      {selectedRows.length > 0 && (
        <div className={styles.actionBar}>
          <span className={`uc-p-body-small ${styles.actionTally}`}>
            {selectedRows.length}{' '}
            {selectedRows.length === 1 ? 'evaluador' : 'evaluadores'} ·{' '}
            {selectedAssignments}{' '}
            {selectedAssignments === 1 ? 'invitación' : 'invitaciones'}
          </span>
          <div className={styles.actionBarRight}>
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              Quitar selección
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setSendModalOpen(true)}
            >
              Enviar invitaciones
            </Button>
          </div>
        </div>
      )}

      <SendInvitationsModal
        isOpen={sendModalOpen}
        sending={sending}
        summary={{
          evaluators: selectedRows.length,
          assignments: selectedAssignments,
          newEvaluators: selectedRows.filter((r) => r.needsPasswordSetup).length,
        }}
        result={sendResult}
        onCancel={handleCloseSendModal}
        onConfirm={handleSend}
      />

      {remindModal && (
        <RemindModal
          isOpen={true}
          evaluatorName={remindModal.evaluatorName}
          evaluatorId={remindModal.evaluatorId}
          assignments={remindModal.assignments}
          onClose={() => setRemindModal(null)}
          onSent={() => {
            setRemindModal(null);
            onToast?.('Recordatorio enviado correctamente.');
          }}
        />
      )}

      {notesModal && (
        <EvaluatorNotesModal
          isOpen={!!notesModal}
          evaluatorId={notesModal.evaluatorId}
          evaluatorName={notesModal.evaluatorName}
          isExternal={notesModal.isExternal}
          onClose={() => setNotesModal(null)}
          onProfileChanged={() => handleProfileChanged(notesModal.evaluatorId)}
        />
      )}

      {assignModal && (
        <EvaluatorAssignmentModal
          application={assignModal}
          isOpen={true}
          onClose={() => setAssignModal(null)}
          onAssignmentComplete={() => {
            onToast?.('Evaluador asignado correctamente.');
            setAssignModal(null);
          }}
        />
      )}
    </div>
  );
};

export default EvalByEvaluator;
